import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormDialogModeEnum } from 'src/app/enums/form-dialog-mode.enum';
import { SkillCategory, SkillCategoryBody } from 'src/app/models/skill_category.model';
import { ApiService } from 'src/app/services';

@Component({
  selector: 'app-skill-category-form-dialog',
  templateUrl: './skill-category-form-dialog.component.html',
  styleUrl: './skill-category-form-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatDialogTitle, MatDialogContent, MatDialogActions, MatButtonModule, MatFormFieldModule, MatInputModule],
})
export class SkillCategoryFormDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<SkillCategoryFormDialogComponent, boolean>);
  private readonly api_service = inject(ApiService);
  private readonly form_builder = inject(FormBuilder);

  readonly data = inject<SkillCategoryFormDialogData>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly is_edit_mode = this.data.mode === FormDialogModeEnum.Edit;

  readonly form = this.form_builder.group({
    name: [this.data.skill_category?.name ?? '', [Validators.required, Validators.maxLength(255)]],
    description: [this.data.skill_category?.description ?? '', Validators.required],
  });

  save(): void {
    if (this.loading() || this.form.invalid) {
      return;
    }

    this.loading.set(true);

    const raw = this.form.getRawValue();
    const body: SkillCategoryBody = {
      name: raw.name ?? '',
      description: raw.description ?? '',
    };

    if (!this.is_edit_mode) {
      body.parent_skill_category_id = this.data.parent_skill_category_id ?? null;
    }

    const request$ =
      this.is_edit_mode && this.data.skill_category
        ? this.api_service.updateSkillCategory(this.data.skill_category.id, body)
        : this.api_service.createSkillCategory(body);

    request$.subscribe({
      next: (): void => {
        this.dialog_ref.close(true);
      },
      error: (): void => {
        this.loading.set(false);
      },
    });
  }
}

export interface SkillCategoryFormDialogData {
  mode: FormDialogModeEnum;
  skill_category?: SkillCategory;
  parent_skill_category_id?: number | null;
}
